import { useEffect, useRef, useState } from "react";
import { FaChevronRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import "./legal.css";

export default function PrivacyPolicy() {
    // show/hide breadcrumb while user scrolls
    const [showBreadcrumb, setShowBreadcrumb] = useState(true);
    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            // hide immediately
            setShowBreadcrumb(false);

            // clear previous timer
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            // show again after user stops scrolling for 600ms
            scrollTimeoutRef.current = setTimeout(() => {
                setShowBreadcrumb(true);
            }, 600);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    };

    return (
        <div className="legal-container">
            <div className={`legal-breadcrumb ${showBreadcrumb ? "visible" : "hidden"}`}>
                <Link to="/legal" className="legal-breadcrumb-link block text-xs mb-1" onClick={scrollToTop}>
                    Legal
                </Link>
                <FaChevronRight className="legal-breadcrumb-icon" />
                <span className="legal-breadcrumb-current block text-xs mb-1">Privacy Policy</span>
                <FaChevronRight className="legal-breadcrumb-icon" />
                <Link to="/legal/customer-term" className="legal-breadcrumb-link block text-xs mb-1" onClick={scrollToTop}>
                    Customer Term
                </Link>
                <FaChevronRight className="legal-breadcrumb-icon" />
                <Link to="/legal/service-provider-term" className="legal-breadcrumb-link block text-xs mb-1" onClick={scrollToTop}>
                    Service Provider Term
                </Link>
            </div>
            <div className="legal-breadcrumb-content">
                <h1 className="legal-title">General Privacy Policy</h1>
                <p className="legal-description">
                    Privacy Policy <br />
                    Last updated: January 1, 2026
                </p>

                <div className="legal-content">
                    <p>
                        This privacy notice lets you know how we promise to look after your personal data. This notice tells you things like what information we use to provide you with our bundle of connection services, how we use your information to keep you safe, and the choices and controls available to you. This notice also tells you about your privacy rights and how data protection rules work to protect everyone.
                    </p>

                    <p>The term “us” or “we” refers to FixerFind a private limited company incorporated and registered under the laws of The Federal Republic of Nigeria.
                    </p>

                    <p>
                        The term “you” or “your” refers to the service provider or the customer who requests, hires and/or receives service through their FixerFind application account.
                    </p>

                    <h2 className="legal-section-title">
                        1. Personal data we process
                    </h2>

                    <p>
                        We only process information that we need, so we can provide you with the best service on our connection marketplace.
                    </p>

                    <ul className="legal-list">
                        <li>
                            <strong> Contact details:</strong> things like name, phone number and e-mail address. For some of our services we might require a little more, like your home address.
                        </li>
                        <li>
                            <strong> Profile information:</strong> things like your profile picture, saved addresses, language and communication preferences. For some of our services we might require a little more, like your service provider license details.
                        </li>
                        <li>
                            <strong> Geolocation:</strong> such as where you need a service from.
                        </li>
                        <li>
                            <strong>Payment information</strong> like the amount charged and the payment card used.
                        </li>
                        <li>
                            <strong>Communication and correspondence records</strong> such as when you engage with our in-app chat, or speak with our customer service agents.
                        </li>
                        <li>
                            <strong> Limited identification data of the device </strong> like the IP address, on which the FixerFind app has been installed

                        </li>
                        <li>
                            <strong> Data related to owning a Service Provider Account (Business Account) </strong> which lets the user upload photos and videos of user's content.
                        </li>
                        <li>
                            <strong> Data about the usage of the connection services:</strong> things like data about journey status, times and data about your conduct as assessed by service providers.
                        </li>
                        <li>
                            <strong>Data related to communication through instant messages directly in the FixerFind app ("Chat with service provider" option):</strong> date and time of the communication and its content.
                        </li>
                    </ul>
                    <h2 className="legal-section-title">2. Purpose of the processing</h2>

                    <p>
                        We process your personal data so we can provide you with one or more of our connection services:
                    </p>
                    <ul className="legal-list">
                        <li>
                            <strong>We connect you with a service provider:</strong> We collect and process personal data for the purpose of connecting customers with service providers so you can be responded to; or to show you where our nearest available services required are located. Instant messages directly in the FixerFind app are processed in order to provide service and customer support (including resolving disputes between service provider and customer), for security purposes as well as to improve our products and services, and for analysis.
                        </li>
                        <li>
                            <strong>We provide you with the closest availabe services you request:</strong> We display geolocation data and the phone number of customers to service providers when service-hailing to enable efficient connection, and make the service transparent. This data is collected only when the FixerFind app is activated. The collection of geolocation data stops after closing the FixerFind app. In some countries service providers cannot see customers' phone numbers; the service provider sees an altogether different number which temporarily diverts to the customer's phone number meaning the service provider and customer can still communicate.

                        </li>
                        <li>
                            <strong>We make sure your journey goes smoothly:</strong> We use geolocation data to make sure you get to your destination and resolve quality issues related to our services. We also need to know where you choose to have the service.

                        </li>
                        <li>
                            <strong>We make sure the app is optimal:</strong> We use contact details to notify customers and service providerss of updates to the FixerFind app so you can keep using our services. We also gather limited data from the device that you use to connect you with our internet, mobile and telephone services, and to help keep your account safe through authentication and verification checks.

                        </li>
                        <li>
                            <strong>We make sure your service is the quickest, cheapest and most convenient to you:</strong> We collect data on the places of service taken by our customers and service providers to analyse geographic coverage. This lets us do things like improve recommendations to the service providers about the most efficient places of recommended service, and helps us make sure that our service providers are in the most convenient locations for customers.

                        </li>
                        <li>
                            <strong>We maintain and promote standards:</strong> We collect data about journey statuses, time and customer ratings from service provider feedback to encourage user safety, promote compliance with our terms and conditions, and make sure we’re providing a quality and enjoyable service to everyone. Customer support data and correspondence is collected for the purposes of feedback, and resolving disputes and service quality issues.

                        </li>
                        <li>
                            <strong>We keep you informed:</strong> Your name, phone number and email address will be used to communicate with you for things like letting you know that your journey has been completed, sending you trip confirmation slips and receipts, and letting you know about important service updates such as when bad weather suspends the hiring of ours scooters.

                        </li>
                    </ul>
                    <h2 className="legal-section-title">
                        3. Legal Basis
                    </h2>

                    <p>
                        We are allowed to use personal information in the ways described above if we have a proper reason to do so. We always make sure we have a good reason for doing anything with your data.
                    </p>


                    <ul className="legal-list">
                        <li>
                            Personal data is generally processed in order to provide the services contracted through the FixerFind app with you. This means that to give you the service we promised you, and to meet our terms and conditions, we’ll process your personal data to meet those obligations.

                        </li>
                        <li>
                            We generally in other circumstances process your personal data relying on legitimate interest grounds. Legitimate interests include our commercial interests in providing an innovative, personalised, safe and profitable service to our customers and partners, unless those interests are overridden by other interests. Our legitimate interests also include things like investigating and detecting fraudulent payments and other malicious activities, maintaining the security of our network and systems, and responding to suspected or actual criminal acts.

                        </li>
                    </ul>
                    <p>
                        We may from time to time rely on alternative legal bases when:

                    </p>

                    <ul className="legal-list">
                        <li>
                            it is necessary to comply with a legal obligation such as for processing data when the law requires it, including, for example, if there is a valid legal request to disclose personal information to a third party such as a Court or regulatory authority;

                        </li>
                        <li>
                            to protect your vital interests, or those of others, for example in the event of an emergency or an imminent threat to life; or

                        </li>
                        <li>
                            you have given us clear consent to do so for another specific purpose in which you have been fully informed.

                        </li>
                    </ul>
                    <p>
                        In countries where legitimate interest is not an available lawful basis for FixerFind’s processing activities, such as in Nigeria, FixerFind instead relies on an alternative valid legal basis available above.

                    </p>

                    <p>
                        If you choose not to give us personal information it may prevent us from fulfilling the contract with you, or stop us doing something expected of us in law. It may also mean that we cannot operate your account. For example, we collect and process the personal data submitted by you in the course of installing and using the FixerFind app; refusing to share geolocation data via the App means we cannot direct a service provider to your location for pick-up, nor can we show you one of our own vehicles close to you for hire. We will not be able to fulfil our obligations to you in other circumstances, for example, should you refuse to undertake an identity verification check to ensure the integrity of your account, then the account may be suspended or blocked to prevent fraud.
                    </p>

                    <h2 className="legal-section-title">
                        4. Recipients
                    </h2>

                    <p>
                        We only work with trusted partners and authorities. We only share when there is a proper reason to do so. We limit sharing to only that which is required. We do not sell your personal information.
                    </p>

                    <ul className="legal-list">
                        <li>
                            The personal data of the customer is only disclosed to the service-hailing service providers when they engage with a service on in the FixerFind app; in such a case, the service provider will see the name, phone number (in some countries the number is masked) and geolocation data of the customer.

                        </li>
                        <li>
                            After providing the connection service, the name and the telephone number (in some countries the number is masked) of the service-hailing customer will remain visible to the service provider for 24-48 hours.

                        </li>
                        <li>
                            Feedback given by customers regarding the quality of the service-hailing service is anonymous. Your rating is private between you and FixerFind.

                        </li>
                        <li>
                            Depending on the location of the customer, the personal data may be disclosed to the FixerFind group companies and partners (local subsidiaries, representatives, affiliates, agents etc). Processing of personal data by FixerFind group companies and partners will occur under the same strict conditions as established in this privacy notice. You can read more on your country page.

                        </li>
                        <li>
                            In some circumstances we are legally obliged to share information with external recipients. For example, under a Court Order or where we cooperate with a data protection supervisory authority in handling complaints or investigations. We can also respond to requests, such as those from law enforcement agencies, when we have a good-faith belief that the response is required by law in that jurisdiction or furthers a public interest task, affects users in that jurisdiction, and is consistent with internationally recognised standards. In any scenario, we’ll always satisfy ourselves that we have a lawful basis on which to share the information, and we’ll make sure that we document our decision.

                        </li>
                    </ul>

                    <h2 className="legal-section-title">
                        5. Security and access
                    </h2>

                    <ul className="legal-list">
                        <li>
                            FixerFind group companies and partners can access personal data to the extent necessary to provide customer support in the respective country.

                        </li>
                        <li>
                            For our research and scientific purposes, all data, like bulk geolocation data, is anonymised so you can never be identified from it.

                        </li>
                    </ul>
                    <h2 className="legal-section-title">
                        6. Your rights and controls
                    </h2>

                    <p>
                        We want you to stay in control of your personal data. FixerFind provides you with controls through the app where you can view your personal information including your profile data and trip history. We also offer in-app settings like marketing opt-ins, and transparent cookie consent controls on our website.
                    </p>

                    <i>
                        <strong> Your right of access</strong>
                    </i>

                    <p>
                        You can access your personal data via the FixerFind app. You have the right to ask us for copies of your personal information. There are some exemptions, such as when we have to balance the rights of others, which means you may not always receive all the information we process.

                    </p>
                    <i>
                        <strong>Your right to rectification</strong>
                    </i>

                    <p>
                        You can access and update your personal data via the FixerFind app. You have the right to ask us to rectify information you think is otherwise inaccurate. You also have the right to ask us to complete information you think is incomplete.
                    </p>
                    <i>
                        <strong>   Your right to erasure</strong>
                    </i>
                    <p>
                        You have the right to ask us to erase your personal information in certain circumstances.
                    </p>
                    <i>
                        <strong> Your right to restriction of processing</strong>
                    </i>

                    <p>
                        You have the right to ask us to restrict the processing of your information in certain circumstances. This means that your data can only be used for certain things, such as legal claims or to exercise legal rights.
                    </p>
                    <i>
                        <strong>   Your right to object to processing</strong>
                    </i>

                    <p>
                        You may have the right to object to processing if we are processing your information on the basis of legitimate interests. You may submit an objection to any automated decision we have made, and ask that a person reviews it.
                    </p>
                    <i>
                        <strong>  Your right to data portability</strong>
                    </i>
                    <ul className="legal-list">
                        <li>
                            You have the right to ask that we transfer the information you gave us from one organisation to another, or give it to you, in certain circumstances. This only applies to information you have given us.

                        </li>
                        <li>
                            The retention period for the data about instant messages directly in the FixerFind app messages is 90 days, except cases where messages are related to a reported incident - in which case we will store them for 6 month.

                        </li>
                    </ul>
                    <h2 className="legal-section-title">
                        7. Retention
                    </h2>

                    <p>
                        FixerFind retains user data for only as long as necessary for the purposes described above. This means that we retain different categories of data for different periods of time depending on the type of data, the connection service it relates to, and the purposes for which we collected the data.
                    </p>

                    <ul className="legal-list">
                        <li>
                            Your personal data will be stored as long as you have an active customer account. If your account is closed, personal data will be deleted (according to our retention schedule and rules), unless such data is still required to meet any legal obligation, or for accounting, dispute resolution or fraud prevention purposes.

                        </li>
                        <li>
                            Financial data regarding connection services provided to customers will be stored for three years after the last journey. Data required for other accounting purposes will be stored for seven years after the last journey.

                        </li>
                        <li>
                            In the event that there are suspicions of a criminal offence, fraud or false information having been provided, the data will be stored as long as necessary according to the internal, legal, and regulatory requirements.

                        </li>
                        <li>
                            In case of payment disputes, data will be retained until the claim is satisfied or the expiry date of such claims.

                        </li>
                        <li>
                            Journey history data and the data about usage of connection services will be stored for three years, after which the data will be anonymized.

                        </li>
                    </ul>

                    <p>
                        Please note that the deinstallation of FixerFind app in your device does not cause the deletion of your personal data. If the FixerFind app has not been used for three years, we’ll get in touch and ask you to confirm whether you wish to keep your account active for future use. If no reply is received in a reasonable time, the account will be closed and personal data will be deleted unless such data is required for any purpose mentioned earlier in this privacy notice.
                    </p>
                    <h2 className="legal-section-title">
                        8. Direct marketing
                    </h2>
                    <ul className="legal-list">
                        <li>
                            We may send direct marketing messages to your e-mail address and/or phone number when you have given us permission to do so, or under a soft opt-in basis. We may personalise direct marketing messages using information on how you use FixerFind’s services, such as how frequently you use the FixerFind app, and your connection preferences.

                        </li>
                        <li>
                            If you no longer wish to receive direct marketing messages, please click the "Unsubscribe" link in the footer of one of our emails, or opt-out in the profile section of the FixerFind app. Easy.

                        </li>
                    </ul>
                    <h2 className="legal-section-title">
                        9. Automated decision making
                    </h2>

                    <p>
                        We use help of automated decision-making in case of issuing warnings and implementing service hailing service suspension. Using automated decision-making is less error-prone, more efficient and safer than using our employees. Data about your usage of the connection services (data about journey status incl. cancellations and no-shows and data about your conduct as assessed by service providers) is considered when assessing the need for a warning or suspension. You can read more about the automated decision-making here. service hailing suspension will stay in force for 6 months, after which access to service hailing service will be restored.
                    </p>

                    <p>
                        You will always have the right for human review of the decision and to contest the decision, express your point of view and obtain an explanation by contacting our customer support in our app.
                    </p>
                    <h2 className="legal-section-title">
                        10. Dispute resolution
                    </h2>

                    <ul className="legal-list">
                        <li>
                            Disputes relating to the processing of personal data are resolved through customer support: info@usefixerfind.com

                        </li>
                    </ul>
                    <h2 className="legal-section-title">
                        11. Making this notice great
                    </h2>

                    <p>
                        We hope you have found this privacy notice easy to understand.
                    </p>

                    <p>
                        Data protection laws are important. They strengthen rules and enhance your information rights. Many of these rules may appear complicated, but most are grounded in common sense. We take our responsibilities with your data seriously. FixerFind will continue to make changes to this privacy notice as part of our commitment to protecting your privacy and affording you even more transparency.
                    </p>

                    <p>
                        Further information is available in the local Terms and Conditions for customers which can be located <Link to={"/legal/customer-term"} className="legal-link-pages" onClick={scrollToTop}>here</Link>.
                    </p>
                </div>
            </div>
        </div>
    );
}
